import React, { useState } from 'react';
import styled from 'styled-components';
import Link from 'next/link';
import { List } from '@styled-icons/fa-solid';
import useTranslation from 'next-translate/useTranslation';
import { Role } from '../../../api/lib/api/user';
import { useRafflesByLabel, useRafflesJoinedByUser } from '../../../api';

import UserList from '../../../containers/TracksList/User';
import LikedList from '../../../containers/TracksList/Liked';
import ModalPortal from '../../common/Modal';
import { TextButtonLink } from '../../common/Link';
import PlaylistModal from '../../../containers/Modals/Playlist';

import { Tabs as TabsContainer } from '../../common/Tabs';
import { GradientButton } from '../../common/Buttons';
import { Desktop, noDesktop } from '../../../style/constants';
import RafflesGridHooks from './RafflesGrid';
import CentralizedTab from '../../../VuexyDesign/atoms/CentralizedTab';

const Index = ({
  profileId,
  role,
  currentUser,
  userHasTracks,
}: {
  role?: Role | null;
  profileId: number;
  currentUser?: boolean;
  userHasTracks?: boolean;
}) => {
  const { t } = useTranslation('profile');
  const [showPlaylistModal, setShowPlaylistModal] = useState(false);

  const useFetchCreatedRaffles = () =>
    useRafflesByLabel(profileId, { filter: 'contest', sort: 'latest' });
  const useFetchCreatedContests = () =>
    useRafflesByLabel(profileId, { filter: 'remix_contest', sort: 'latest' });

  const useFetchJoinedRaffles = () =>
    useRafflesJoinedByUser(profileId, { filter: 'contest', sort: 'latest' });

  const useFetchJoinedContests = () =>
    useRafflesJoinedByUser(profileId, {
      filter: 'remix_contest',
      sort: 'latest',
    });

  const showTracks = role === 'producer';
  const showRafflesCreated = Boolean(role === 'label');
  const showJoinedRaffles = Boolean(currentUser && role === 'producer');

  const propscomponent = (
    <Desktop>
      <VirppsLink href="/my-tracks">
        <>
          <List size={14} />
          <span>{t('profile:manageTracks')}</span>
        </>
      </VirppsLink>
    </Desktop>
  );

  return (
    <Container>
      <ModalPortal isOpened={showPlaylistModal} toggle={setShowPlaylistModal}>
        <PlaylistModal />
      </ModalPortal>
      <TabsContainer className="profile-tabs__container">
        <CentralizedTab
          dropdownitemclassName="dropdown-container"
          tabs={[
            ...(showTracks
              ? [
                  {
                    label: t('profile:tracks'),
                    component: (
                      <>
                        <UserList
                          id={profileId}
                          isCurrentUserProfilePage={currentUser}
                        />
                        {!userHasTracks && (
                          <NoTracksMessage current={currentUser} />
                        )}
                      </>
                    ),
                  },
                ]
              : []),
            ...(showRafflesCreated
              ? [
                  {
                    label: t('profile:raffles'),
                    component: (
                      <>
                        <RafflesGridHooks useFetch={useFetchCreatedRaffles} />
                      </>
                    ),
                  },
                ]
              : []),

            ...(showRafflesCreated
              ? [
                  {
                    label: t('profile:contests'),
                    component: (
                      <RafflesGridHooks useFetch={useFetchCreatedContests} />
                    ),
                  },
                ]
              : []),

            {
              label: 'like',
              component: <LikedList id={profileId} />,
            },
            ...(showJoinedRaffles
              ? [
                  {
                    label: t('profile:raffles'),
                    component: (
                      <RafflesGridHooks useFetch={useFetchJoinedRaffles} />
                    ),
                  },
                ]
              : []),
            ...(showJoinedRaffles
              ? [
                  {
                    label: t('profile:contests'),
                    component: (
                      <RafflesGridHooks useFetch={useFetchJoinedContests} />
                    ),
                  },
                ]
              : []),
          ]}
          size="md"
          className="tab-container"
          propsComponent={propscomponent}
          tabContentclassName="tab-content"
        />
      </TabsContainer>
    </Container>
  );
};

export default Index;

const NoTracksMessage = ({ current }: { current?: boolean }) => {
  const { t } = useTranslation('profile');
  return (
    <NoTracksMessageContainer>
      {current ? (
        <>
          <span>{t('profile:noUploadedTracks')}</span>
          <Desktop>
            <Link href="/upload">
              <GradientButton>{t('profile:startUploading')}</GradientButton>
            </Link>
          </Desktop>
        </>
      ) : (
        <div>{t('profile:userNoTracks')}</div>
      )}
    </NoTracksMessageContainer>
  );
};

const NoTracksMessageContainer = styled.div`
  display: flex;
  align-items: center;
  flex-direction: column;

  span {
    margin-bottom: 15px;
  }
`;

const Container = styled.div`
  width: 100%;

  .reactstrap-select-custom-style .select__control {
    margin: 0 0 0 0 !important;
  }
  .dropdown-container {
    width: 100%;
  }
  .profile-tabs__container {
    @media ${noDesktop} {
      align-items: center;
      //  justify-content: center;
      // padding-left: 2rem;
    }
    div[data-name='row'] {
      border-bottom: 1px solid whitesmoke;
      border-left: none;
      border-right: none;
    }
  }

  .profile-tabs-list__container {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .tab-content {
    padding-top: 20px;
  }
  .tab-container {
    display: flex;
    justify-content: space-between;
    align-items: center;

    @media ${noDesktop} {
      padding-top: 1.3rem;
    }
  }

  .profile-tabs-list {
    padding: 15px;
  }
`;

const VirppsLink = styled(TextButtonLink)`
  padding: 1rem 1.5rem;

  :hover {
    background-color: whitesmoke;
    color: black;
    font-size: bold;
  }

  svg {
    margin-right: 10px;
    vertical-align: -2px;
  }
`;
